import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Loader2, Save } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '~/api/trpc';
import CategoriesEditor from './service-selection/CategoriesEditor';
import RecommendedItemsEditor from './service-selection/RecommendedItemsEditor';
import SymptomCategoriesEditor from './service-selection/SymptomCategoriesEditor';

type ConfigFormData = {
    pageTitleDe: string;
    pageTitleEn: string;
    progressDisplayDe: string;
    progressDisplayEn: string;
    recommendedSectionTitleDe: string;
    recommendedSectionTitleEn: string;
    categoriesSectionTitleDe: string;
    categoriesSectionTitleEn: string;
    symptomsSectionTitleDe: string;
    symptomsSectionTitleEn: string;
    searchPlaceholderDe: string;
    searchPlaceholderEn: string;
    noResultsTextDe: string;
    noResultsTextEn: string;
    moreInfoButtonTextDe: string;
    moreInfoButtonTextEn: string;
    descriptionSectionTitleDe: string;
    descriptionSectionTitleEn: string;
    benefitsSectionTitleDe: string;
    benefitsSectionTitleEn: string;
    customerReviewTitleDe: string;
    customerReviewTitleEn: string;
    youShouldKnowTitleDe: string;
    youShouldKnowTitleEn: string;
    pricePrefixTextDe: string;
    pricePrefixTextEn: string;
    selectButtonTextDe: string;
    selectButtonTextEn: string;
    chooseOptionTitleDe: string;
    chooseOptionTitleEn: string;
    addOnPopupTitleDe: string;
    addOnPopupTitleEn: string;
    addOnPopupDescriptionDe: string;
    addOnPopupDescriptionEn: string;
    skipAddOnButtonTextDe: string;
    skipAddOnButtonTextEn: string;
    addToCartButtonTextDe: string;
    addToCartButtonTextEn: string;
    continueButtonTextDe: string;
    continueButtonTextEn: string;
    // Other fields
    removedFromCartToastDe: string;
    removedFromCartToastEn: string;
    backButtonTextDe: string;
    backButtonTextEn: string;
    noCategoriesMessageDe: string;
    noCategoriesMessageEn: string;
    addTreatmentsToContinueDe: string;
    addTreatmentsToContinueEn: string;
    totalPriceLabelDe: string;
    totalPriceLabelEn: string;
    treatmentsLabelDe: string;
    treatmentsLabelEn: string;
    // Bundle Upsell Popup fields
    bundleUpsellTitleDe: string;
    bundleUpsellTitleEn: string;
    bundleUpsellSubtitleDe: string;
    bundleUpsellSubtitleEn: string;
    bundleUpsellSingleTreatmentLabelDe: string;
    bundleUpsellSingleTreatmentLabelEn: string;
    bundleUpsellBundleLabelDe: string;
    bundleUpsellBundleLabelEn: string;
    bundleUpsellBenefit1De: string;
    bundleUpsellBenefit1En: string;
    bundleUpsellBenefit2De: string;
    bundleUpsellBenefit2En: string;
    bundleUpsellBenefit3De: string;
    bundleUpsellBenefit3En: string;
    bundleUpsellDeclineButtonTextDe: string;
    bundleUpsellDeclineButtonTextEn: string;
    bundleUpsellAcceptButtonTextDe: string;
    bundleUpsellAcceptButtonTextEn: string;
    bundleUpsellRedemptionTitleDe: string;
    bundleUpsellRedemptionTitleEn: string;
    bundleUpsellRedemptionTextDe: string;
    bundleUpsellRedemptionTextEn: string;
    bundleUpsellRedemptionButtonTextDe: string;
    bundleUpsellRedemptionButtonTextEn: string;
    // Treatment Plan fields
    treatmentPlanTitleDe: string;
    treatmentPlanTitleEn: string;
    treatmentPlanSubtitleDe: string;
    treatmentPlanSubtitleEn: string;
};

const CopyConfigEditor = () => {
    const [formData, setFormData] = useState<ConfigFormData>({
        pageTitleDe: '',
        pageTitleEn: '',
        progressDisplayDe: '',
        progressDisplayEn: '',
        recommendedSectionTitleDe: '',
        recommendedSectionTitleEn: '',
        categoriesSectionTitleDe: '',
        categoriesSectionTitleEn: '',
        symptomsSectionTitleDe: '',
        symptomsSectionTitleEn: '',
        searchPlaceholderDe: '',
        searchPlaceholderEn: '',
        noResultsTextDe: '',
        noResultsTextEn: '',
        moreInfoButtonTextDe: '',
        moreInfoButtonTextEn: '',
        descriptionSectionTitleDe: '',
        descriptionSectionTitleEn: '',
        benefitsSectionTitleDe: '',
        benefitsSectionTitleEn: '',
        customerReviewTitleDe: '',
        customerReviewTitleEn: '',
        youShouldKnowTitleDe: '',
        youShouldKnowTitleEn: '',
        pricePrefixTextDe: '',
        pricePrefixTextEn: '',
        selectButtonTextDe: '',
        selectButtonTextEn: '',
        chooseOptionTitleDe: '',
        chooseOptionTitleEn: '',
        addOnPopupTitleDe: '',
        addOnPopupTitleEn: '',
        addOnPopupDescriptionDe: '',
        addOnPopupDescriptionEn: '',
        skipAddOnButtonTextDe: '',
        skipAddOnButtonTextEn: '',
        addToCartButtonTextDe: '',
        addToCartButtonTextEn: '',
        continueButtonTextDe: '',
        continueButtonTextEn: '',
        // Other fields
        removedFromCartToastDe: '',
        removedFromCartToastEn: '',
        backButtonTextDe: '',
        backButtonTextEn: '',
        noCategoriesMessageDe: '',
        noCategoriesMessageEn: '',
        addTreatmentsToContinueDe: '',
        addTreatmentsToContinueEn: '',
        totalPriceLabelDe: '',
        totalPriceLabelEn: '',
        treatmentsLabelDe: '',
        treatmentsLabelEn: '',
        // Bundle Upsell Popup fields
        bundleUpsellTitleDe: '',
        bundleUpsellTitleEn: '',
        bundleUpsellSubtitleDe: '',
        bundleUpsellSubtitleEn: '',
        bundleUpsellSingleTreatmentLabelDe: '',
        bundleUpsellSingleTreatmentLabelEn: '',
        bundleUpsellBundleLabelDe: '',
        bundleUpsellBundleLabelEn: '',
        bundleUpsellBenefit1De: '',
        bundleUpsellBenefit1En: '',
        bundleUpsellBenefit2De: '',
        bundleUpsellBenefit2En: '',
        bundleUpsellBenefit3De: '',
        bundleUpsellBenefit3En: '',
        bundleUpsellDeclineButtonTextDe: '',
        bundleUpsellDeclineButtonTextEn: '',
        bundleUpsellAcceptButtonTextDe: '',
        bundleUpsellAcceptButtonTextEn: '',
        bundleUpsellRedemptionTitleDe: '',
        bundleUpsellRedemptionTitleEn: '',
        bundleUpsellRedemptionTextDe: '',
        bundleUpsellRedemptionTextEn: '',
        bundleUpsellRedemptionButtonTextDe: '',
        bundleUpsellRedemptionButtonTextEn: '',
        // Treatment Plan fields
        treatmentPlanTitleDe: '',
        treatmentPlanTitleEn: '',
        treatmentPlanSubtitleDe: '',
        treatmentPlanSubtitleEn: '',
    });
    const [isDirty, setIsDirty] = useState(false);

    const utils = trpc.useUtils();
    const { data: configData, isLoading } = trpc.bookingFunnelCMS.getServiceSelectionConfig.useQuery();

    const updateMutation = trpc.bookingFunnelCMS.updateServiceSelectionConfig.useMutation({
        onSuccess: () => {
            toast.success('Konfiguration gespeichert');
            setIsDirty(false);
            utils.bookingFunnelCMS.getServiceSelectionConfig.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Speichern', { description: error.message });
        },
    });

    useEffect(() => {
        if (configData?.data) {
            const config = configData.data;
            setFormData({
                pageTitleDe: config.pageTitleDe,
                pageTitleEn: config.pageTitleEn,
                progressDisplayDe: config.progressDisplayDe,
                progressDisplayEn: config.progressDisplayEn,
                recommendedSectionTitleDe: config.recommendedSectionTitleDe,
                recommendedSectionTitleEn: config.recommendedSectionTitleEn,
                categoriesSectionTitleDe: config.categoriesSectionTitleDe,
                categoriesSectionTitleEn: config.categoriesSectionTitleEn,
                symptomsSectionTitleDe: config.symptomsSectionTitleDe,
                symptomsSectionTitleEn: config.symptomsSectionTitleEn,
                searchPlaceholderDe: config.searchPlaceholderDe,
                searchPlaceholderEn: config.searchPlaceholderEn,
                noResultsTextDe: config.noResultsTextDe,
                noResultsTextEn: config.noResultsTextEn,
                moreInfoButtonTextDe: config.moreInfoButtonTextDe,
                moreInfoButtonTextEn: config.moreInfoButtonTextEn,
                descriptionSectionTitleDe: config.descriptionSectionTitleDe,
                descriptionSectionTitleEn: config.descriptionSectionTitleEn,
                benefitsSectionTitleDe: config.benefitsSectionTitleDe,
                benefitsSectionTitleEn: config.benefitsSectionTitleEn,
                customerReviewTitleDe: config.customerReviewTitleDe,
                customerReviewTitleEn: config.customerReviewTitleEn,
                youShouldKnowTitleDe: config.youShouldKnowTitleDe,
                youShouldKnowTitleEn: config.youShouldKnowTitleEn,
                pricePrefixTextDe: config.pricePrefixTextDe,
                pricePrefixTextEn: config.pricePrefixTextEn,
                selectButtonTextDe: config.selectButtonTextDe,
                selectButtonTextEn: config.selectButtonTextEn,
                chooseOptionTitleDe: config.chooseOptionTitleDe,
                chooseOptionTitleEn: config.chooseOptionTitleEn,
                addOnPopupTitleDe: config.addOnPopupTitleDe,
                addOnPopupTitleEn: config.addOnPopupTitleEn,
                addOnPopupDescriptionDe: config.addOnPopupDescriptionDe,
                addOnPopupDescriptionEn: config.addOnPopupDescriptionEn,
                skipAddOnButtonTextDe: config.skipAddOnButtonTextDe,
                skipAddOnButtonTextEn: config.skipAddOnButtonTextEn,
                addToCartButtonTextDe: config.addToCartButtonTextDe,
                addToCartButtonTextEn: config.addToCartButtonTextEn,
                continueButtonTextDe: config.continueButtonTextDe,
                continueButtonTextEn: config.continueButtonTextEn,
                // Other fields
                removedFromCartToastDe: config.removedFromCartToastDe ?? '',
                removedFromCartToastEn: config.removedFromCartToastEn ?? '',
                backButtonTextDe: config.backButtonTextDe ?? '',
                backButtonTextEn: config.backButtonTextEn ?? '',
                noCategoriesMessageDe: config.noCategoriesMessageDe ?? '',
                noCategoriesMessageEn: config.noCategoriesMessageEn ?? '',
                addTreatmentsToContinueDe: config.addTreatmentsToContinueDe ?? '',
                addTreatmentsToContinueEn: config.addTreatmentsToContinueEn ?? '',
                totalPriceLabelDe: config.totalPriceLabelDe ?? '',
                totalPriceLabelEn: config.totalPriceLabelEn ?? '',
                treatmentsLabelDe: config.treatmentsLabelDe ?? '',
                treatmentsLabelEn: config.treatmentsLabelEn ?? '',
                // Bundle Upsell Popup fields
                bundleUpsellTitleDe: config.bundleUpsellTitleDe ?? '',
                bundleUpsellTitleEn: config.bundleUpsellTitleEn ?? '',
                bundleUpsellSubtitleDe: config.bundleUpsellSubtitleDe ?? '',
                bundleUpsellSubtitleEn: config.bundleUpsellSubtitleEn ?? '',
                bundleUpsellSingleTreatmentLabelDe: config.bundleUpsellSingleTreatmentLabelDe ?? '',
                bundleUpsellSingleTreatmentLabelEn: config.bundleUpsellSingleTreatmentLabelEn ?? '',
                bundleUpsellBundleLabelDe: config.bundleUpsellBundleLabelDe ?? '',
                bundleUpsellBundleLabelEn: config.bundleUpsellBundleLabelEn ?? '',
                bundleUpsellBenefit1De: config.bundleUpsellBenefit1De ?? '',
                bundleUpsellBenefit1En: config.bundleUpsellBenefit1En ?? '',
                bundleUpsellBenefit2De: config.bundleUpsellBenefit2De ?? '',
                bundleUpsellBenefit2En: config.bundleUpsellBenefit2En ?? '',
                bundleUpsellBenefit3De: config.bundleUpsellBenefit3De ?? '',
                bundleUpsellBenefit3En: config.bundleUpsellBenefit3En ?? '',
                bundleUpsellDeclineButtonTextDe: config.bundleUpsellDeclineButtonTextDe ?? '',
                bundleUpsellDeclineButtonTextEn: config.bundleUpsellDeclineButtonTextEn ?? '',
                bundleUpsellAcceptButtonTextDe: config.bundleUpsellAcceptButtonTextDe ?? '',
                bundleUpsellAcceptButtonTextEn: config.bundleUpsellAcceptButtonTextEn ?? '',
                bundleUpsellRedemptionTitleDe: config.bundleUpsellRedemptionTitleDe ?? '',
                bundleUpsellRedemptionTitleEn: config.bundleUpsellRedemptionTitleEn ?? '',
                bundleUpsellRedemptionTextDe: config.bundleUpsellRedemptionTextDe ?? '',
                bundleUpsellRedemptionTextEn: config.bundleUpsellRedemptionTextEn ?? '',
                bundleUpsellRedemptionButtonTextDe: config.bundleUpsellRedemptionButtonTextDe ?? '',
                bundleUpsellRedemptionButtonTextEn: config.bundleUpsellRedemptionButtonTextEn ?? '',
                // Treatment Plan fields
                treatmentPlanTitleDe: config.treatmentPlanTitleDe ?? '',
                treatmentPlanTitleEn: config.treatmentPlanTitleEn ?? '',
                treatmentPlanSubtitleDe: config.treatmentPlanSubtitleDe ?? '',
                treatmentPlanSubtitleEn: config.treatmentPlanSubtitleEn ?? '',
            });
        }
    }, [configData]);

    const handleChange = (field: keyof ConfigFormData, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        setIsDirty(true);
    };

    const handleSave = () => {
        updateMutation.mutate(formData);
    };

    if (isLoading) {
        return <Skeleton className='h-96 w-full' />;
    }

    const renderFieldPair = (labelDe: string, labelEn: string, fieldDe: keyof ConfigFormData, fieldEn: keyof ConfigFormData, multiline = false) => (
        <div className='grid grid-cols-2 gap-4'>
            <div className='space-y-2'>
                <Label>{labelDe}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldDe]}
                        onChange={(e) => handleChange(fieldDe, e.target.value)}
                    />
                )}
            </div>
            <div className='space-y-2'>
                <Label>{labelEn}</Label>
                {multiline ? (
                    <Textarea
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                        rows={3}
                    />
                ) : (
                    <Input
                        value={formData[fieldEn]}
                        onChange={(e) => handleChange(fieldEn, e.target.value)}
                    />
                )}
            </div>
        </div>
    );

    return (
        <div className='space-y-6'>
            <div className='flex justify-end'>
                <Button onClick={handleSave} disabled={!isDirty || updateMutation.isPending}>
                    {updateMutation.isPending ? (
                        <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                    ) : (
                        <Save className='mr-2 h-4 w-4' />
                    )}
                    Speichern
                </Button>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Seiten-Header</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Seitentitel (DE)', 'Page Title (EN)', 'pageTitleDe', 'pageTitleEn')}
                    {renderFieldPair('Fortschrittsanzeige (DE)', 'Progress Display (EN)', 'progressDisplayDe', 'progressDisplayEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Abschnitts-Titel</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Empfehlungen Titel (DE)', 'Recommendations Title (EN)', 'recommendedSectionTitleDe', 'recommendedSectionTitleEn')}
                    {renderFieldPair('Kategorien Titel (DE)', 'Categories Title (EN)', 'categoriesSectionTitleDe', 'categoriesSectionTitleEn')}
                    {renderFieldPair('Symptome Titel (DE)', 'Symptoms Title (EN)', 'symptomsSectionTitleDe', 'symptomsSectionTitleEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Treatment Plan Karte</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Titel (DE)', 'Title (EN)', 'treatmentPlanTitleDe', 'treatmentPlanTitleEn')}
                    {renderFieldPair('Untertitel (DE)', 'Subtitle (EN)', 'treatmentPlanSubtitleDe', 'treatmentPlanSubtitleEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Suche</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Such-Platzhalter (DE)', 'Search Placeholder (EN)', 'searchPlaceholderDe', 'searchPlaceholderEn')}
                    {renderFieldPair('Keine Ergebnisse Text (DE)', 'No Results Text (EN)', 'noResultsTextDe', 'noResultsTextEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Info Drawer & Kartenansicht</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Mehr Dazu Link (DE)', 'More Info Link (EN)', 'moreInfoButtonTextDe', 'moreInfoButtonTextEn')}
                    {renderFieldPair('Preis-Präfix (DE)', 'Price Prefix (EN)', 'pricePrefixTextDe', 'pricePrefixTextEn')}
                    {renderFieldPair('Beschreibung Titel (DE)', 'Description Title (EN)', 'descriptionSectionTitleDe', 'descriptionSectionTitleEn')}
                    {renderFieldPair('Vorteile Titel (DE)', 'Benefits Title (EN)', 'benefitsSectionTitleDe', 'benefitsSectionTitleEn')}
                    {renderFieldPair('Kunden Rezension Titel (DE)', 'Customer Review Title (EN)', 'customerReviewTitleDe', 'customerReviewTitleEn')}
                    {renderFieldPair('Das solltest du wissen Titel (DE)', 'You Should Know Title (EN)', 'youShouldKnowTitleDe', 'youShouldKnowTitleEn')}
                    {renderFieldPair('Auswählen Button (DE)', 'Select Button (EN)', 'selectButtonTextDe', 'selectButtonTextEn')}
                    {renderFieldPair('Option wählen Titel (DE)', 'Choose Option Title (EN)', 'chooseOptionTitleDe', 'chooseOptionTitleEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Add-On Popup</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Add-On Popup Titel (DE)', 'Add-On Popup Title (EN)', 'addOnPopupTitleDe', 'addOnPopupTitleEn')}
                    {renderFieldPair('Add-On Popup Beschreibung (DE)', 'Add-On Popup Description (EN)', 'addOnPopupDescriptionDe', 'addOnPopupDescriptionEn', true)}
                    {renderFieldPair('Überspringen Button (DE)', 'Skip Button (EN)', 'skipAddOnButtonTextDe', 'skipAddOnButtonTextEn')}
                    {renderFieldPair('In den Warenkorb Button (DE)', 'Add to Cart Button (EN)', 'addToCartButtonTextDe', 'addToCartButtonTextEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Bundle Upsell Popup</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Titel (DE)', 'Title (EN)', 'bundleUpsellTitleDe', 'bundleUpsellTitleEn')}
                    {renderFieldPair('Untertitel (DE)', 'Subtitle (EN)', 'bundleUpsellSubtitleDe', 'bundleUpsellSubtitleEn')}
                    {renderFieldPair('Bundle Label (DE)', 'Bundle Label (EN)', 'bundleUpsellBundleLabelDe', 'bundleUpsellBundleLabelEn')}
                    {renderFieldPair('Vorteil 1 (DE)', 'Benefit 1 (EN)', 'bundleUpsellBenefit1De', 'bundleUpsellBenefit1En')}
                    {renderFieldPair('Vorteil 2 (DE)', 'Benefit 2 (EN)', 'bundleUpsellBenefit2De', 'bundleUpsellBenefit2En')}
                    {renderFieldPair('Vorteil 3 (DE)', 'Benefit 3 (EN)', 'bundleUpsellBenefit3De', 'bundleUpsellBenefit3En')}
                    {renderFieldPair('Ablehnen Button (DE)', 'Decline Button (EN)', 'bundleUpsellDeclineButtonTextDe', 'bundleUpsellDeclineButtonTextEn')}
                    {renderFieldPair('Akzeptieren Button (DE)', 'Accept Button (EN)', 'bundleUpsellAcceptButtonTextDe', 'bundleUpsellAcceptButtonTextEn')}
                    {renderFieldPair('Einlösen Titel (DE)', 'Redemption Title (EN)', 'bundleUpsellRedemptionTitleDe', 'bundleUpsellRedemptionTitleEn')}
                    {renderFieldPair('Einlösen Text (DE)', 'Redemption Text (EN)', 'bundleUpsellRedemptionTextDe', 'bundleUpsellRedemptionTextEn')}
                    {renderFieldPair('Einlösen Button (DE)', 'Redemption Button (EN)', 'bundleUpsellRedemptionButtonTextDe', 'bundleUpsellRedemptionButtonTextEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Warenkorb & Navigation</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Behandlungen hinzufügen Text (DE)', 'Add Treatments Text (EN)', 'addTreatmentsToContinueDe', 'addTreatmentsToContinueEn')}
                    {renderFieldPair('Gesamtpreis Label (DE)', 'Total Price Label (EN)', 'totalPriceLabelDe', 'totalPriceLabelEn')}
                    {renderFieldPair('Behandlungen Label (DE)', 'Treatments Label (EN)', 'treatmentsLabelDe', 'treatmentsLabelEn')}
                    {renderFieldPair('Weiter Button (DE)', 'Continue Button (EN)', 'continueButtonTextDe', 'continueButtonTextEn')}
                    {renderFieldPair('Zurück Button (DE)', 'Back Button (EN)', 'backButtonTextDe', 'backButtonTextEn')}
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle className='text-lg'>Benachrichtigungen & Meldungen</CardTitle>
                </CardHeader>
                <CardContent className='space-y-4'>
                    {renderFieldPair('Aus Warenkorb entfernt Toast (DE)', 'Removed from Cart Toast (EN)', 'removedFromCartToastDe', 'removedFromCartToastEn')}
                    {renderFieldPair('Keine Kategorien Nachricht (DE)', 'No Categories Message (EN)', 'noCategoriesMessageDe', 'noCategoriesMessageEn')}
                </CardContent>
            </Card>
        </div>
    );
};

const ServiceSelectionCMS = () => {
    return (
        <Card>
            <CardHeader>
                <CardTitle>Leistungsauswahl CMS</CardTitle>
                <CardDescription>
                    Verwalten Sie buchbare Leistungen, Kategorien und Symptom-Kategorien für den Buchungs-Funnel.
                </CardDescription>
            </CardHeader>
            <CardContent>
                <Tabs defaultValue='copy' className='w-full'>
                    <TabsList className='mb-6'>
                        <TabsTrigger value='copy'>Texte & Copy</TabsTrigger>
                        <TabsTrigger value='categories'>Kategorien</TabsTrigger>
                        <TabsTrigger value='symptom-categories'>Symptom-Kategorien</TabsTrigger>
                        <TabsTrigger value='recommended'>Empfehlungen</TabsTrigger>
                    </TabsList>

                    <TabsContent value='copy'>
                        <CopyConfigEditor />
                    </TabsContent>

                    <TabsContent value='categories'>
                        <CategoriesEditor />
                    </TabsContent>

                    <TabsContent value='symptom-categories'>
                        <SymptomCategoriesEditor />
                    </TabsContent>

                    <TabsContent value='recommended'>
                        <RecommendedItemsEditor />
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
};

export default ServiceSelectionCMS;
